// disable sort keys until production analytics are fixed to avoid conflicts
/* eslint-disable sort-keys */
/* eslint-disable @typescript-eslint/naming-convention */
import Api from '../../api';
import UIState from '../../helpers/UIState';
import TransactionRepository from '../TransactionRepository';
import {
  UTAG_DATA_FIELDS,
  EVENT_FIELDS,
  PAGE_VIEW_FIELDS,
  LINK_FIELDS,
} from './AnalyticsFields';
import objectPick from '../../utils/ObjectPick';
import CartItem from '../../domain/entities/Cart/CartItem';
import SHIPPING_METHODS from '../../constants/ShippingMethods';
import { DeliveryOptions } from '../../constants/Constants';
import Store from '../../domain/entities/Store/Store';
import { IUtagData } from '@estee/elc-universal-utils';
import Transaction from '../../domain/entities/Transaction/Transaction';

export interface IShippingAddressRepositoryConfig {
  api: Api;
  uiState: UIState;
  transactionRepository: TransactionRepository;
}

interface IEvent {
  data: IEventData;
  fields: string[]; // fields that will be sent, used to filter data
  eventType: EventType;
}

interface IEventData {
  active_checkout_panel?: string;
  event_name?: string;
  event_category?: string;
  event_action?: string;
  event_label?: string;
  page_type?: string;
  checkout_type?: string;
}

enum EventType {
  Link,
  View,
}

const shippingOptionsMap = {
  [SHIPPING_METHODS.NONE]: '',
  [SHIPPING_METHODS.NAMED_DAY]: 'named_day',
  [SHIPPING_METHODS.STANDARD]: 'standard',
  [SHIPPING_METHODS.SECOND_DAY]: 'second_day',
  [SHIPPING_METHODS.SECOND_DAY_PM]: 'second_day_pm',
  [SHIPPING_METHODS.OVERNIGHT]: 'overnight',
  [SHIPPING_METHODS.STANDARD2]: 'standard',
  [SHIPPING_METHODS.OVERNIGHT2]: 'overnight',
  [SHIPPING_METHODS.SATURDAY]: 'saturday',
  [SHIPPING_METHODS.LOCAL_COLLECTION]: 'local_collection',
  [SHIPPING_METHODS.STORE_COLLECTION]: 'store_collection',
  [SHIPPING_METHODS.ELECTRONIC_SHIPPING]: 'electronic_shipping',
  [SHIPPING_METHODS.BOPIS]: 'bopis',
  [SHIPPING_METHODS.COURIER]: 'postmates',
};

const deliveryOptionsMap = {
  [DeliveryOptions.HOME_DELIVERY]: 'standard',
  [DeliveryOptions.CLICK_AND_COLLECT]: 'bopis',
  [DeliveryOptions.SAME_DAY_DELIVERY]: 'postmates',
  [DeliveryOptions.PACKSTATION]: 'packstation',
  [DeliveryOptions.CONVENIENCE_STORE_PICK_UP]: 'convenienceStore',
  [DeliveryOptions.UPS_STORE_PICK_UP]: 'upsStore',
  [DeliveryOptions.AU_POST_COLLECTIONS]: 'AUPostCollections',
  [DeliveryOptions.ELECTRONIC_DELIVERY]: 'electronic',
};

export default class AnalyticsRepository {
  private api: Api;

  private uiState: UIState;

  private transactionRepository: TransactionRepository;

  private analyticsData: Record<string, {}> = {
    page_type: 'checkout',
    page_site_area: 'checkout',
    checkout_type: 'rco',
  };

  private idleEvents: IEvent[] = [];

  private isEnabled = false;

  private checkTealiumLoadInterval: number;

  constructor(config: IShippingAddressRepositoryConfig) {
    this.api = config.api;
    this.uiState = config.uiState;
    this.transactionRepository = config.transactionRepository;
  }

  public async initAnalytics(): Promise<void> {
    try {
      this.checkTealiumLoadInterval = window.setInterval(this.checkIfTealiumLoaded, 1000);
      this.checkIfTealiumLoaded();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  private checkIfTealiumLoaded = (): void => {
    if (window.utag && window.utag_data && Object.keys(window.utag_data).length > 0) {
      this.enableAnalytics();
      clearInterval(this.checkTealiumLoadInterval);
    }
  };

  private enableAnalytics(): void {
    this.isEnabled = true;

    const utagData = window.utag_data || {};
    this.analyticsData = {
      ...this.analyticsData,
      ...objectPick(utagData, UTAG_DATA_FIELDS),
    };

    this.idleEvents.forEach(eventData => {
      this.sendEvent(eventData);
    });
    this.idleEvents = [];
  }

  private async trackEvent(event: IEvent): Promise<void> {
    if (this.isEnabled) {
      await this.sendEvent(event);
    } else {
      this.idleEvents.push(event);
    }
  }

  private async sendEvent(event: IEvent): Promise<void> {
    const currentData = await this.getCurrentData();
    const data = objectPick(
      {
        ...this.analyticsData,
        ...currentData,
        ...event.data,
      },
      event.fields,
    );

    if (event.eventType === EventType.Link) {
      this.link(data);
    } else {
      this.updateUtagData(this.transformPayload({ ...event.data }));
      this.view(data);
    }
  }

  /**
   * handle event tracking
   * @param  {Object} data - event info
   */
  private link(data: {}): void {
    if (window.utag && window.utag.link) {
      window.utag.link({ ...data });
    }
  }

  /**
   * handle page tracking
   * @param  {Object} data - event info
   */
  private view(data: {}): void {
    if (window.utag && window.utag.view) {
      window.utag.view({ ...data });
    }
  }

  private updateUtagData = (payload: IUtagData): void => {
    Object.keys(payload).forEach((key): void => {
      window.utag_data[key] = payload[key];
    });
  };

  private transformPayload = (payload: IUtagData): IUtagData => JSON.parse(JSON.stringify(payload));

  /**
   * standardize the location path name passed to Analytics events in checkout pages
   * Removes hash symbol and query string parameters from location hash value passed in analytics event
   * @returns {string} Modified location path name + Modified location hash
   */
  private transformPagePath() : string {
    const locationPathName = window.location.pathname;
    const pathName = locationPathName.includes('checkout') && locationPathName.endsWith('tmpl') ?  locationPathName.substring(0, location.pathname.lastIndexOf('/')) : locationPathName;
    const hashValue = window.location.hash?.split('?')[0].replace('#', '');

    return pathName + hashValue;
  }

  private async getCurrentData(): Promise<{}> {
    let transaction: Transaction | null;
    try {
      transaction = await this.transactionRepository.getTransaction();
    } catch (ex) {}

    const pagePath = this.transformPagePath();
    const location = window.location.origin + pagePath;

    this.updateUtagData(this.transformPayload({
      'page_url' : pagePath,
      'location': location,
    }));

    return {
      cart_items: transaction?.cart.cartItems.map(item => item.productCode) || [],
      order_promo_code: transaction?.offers.map(offer => offer.offerCode) || [],
      page_url: pagePath,
      virtual_page_path: pagePath,
      pc_email_optin: window.utag_data && window.utag_data.pc_email_optin,
      location: location,
    };
  }

  async intialAnalyticsPageView(): Promise<void> {
    const eventData = {
      data: {
        page_type: 'checkout',
        page_site_area: 'checkout',
        checkout_type: 'rco',
      },
      fields: PAGE_VIEW_FIELDS,
      eventType: EventType.View,
    };
    await this.trackEvent(eventData);
  }

  async shippingScreenViewEvent(): Promise<void> {
    const eventData = {
      data: {
        active_checkout_panel: 'shipping',
        event_name: 'begin_checkout',
        event_category: 'ecommerce',
        event_action: 'begin_checkout',
        event_label: 'begin_checkout',
        event_noninteraction: true,
        enh_action: 'checkout',
      },
      fields: EVENT_FIELDS,
      eventType: EventType.Link,
    };
    await this.trackEvent(eventData);
  }

  async paymentScreenViewEvent(): Promise<void> {
    const eventData = {
      data: {
        event_name: 'add_payment_info',
        event_category: 'ecommerce',
        event_action: 'add_payment_info',
        event_label: 'add_payment_info',
        event_noninteraction: true,
        enh_action: 'checkout',
        active_checkout_panel: 'payment',
      },
      fields: EVENT_FIELDS,
      eventType: EventType.Link,
    };
    await this.trackEvent(eventData);
  }

  async paymentContinueLinkEvent(paymentType: string): Promise<void> {
    const eventData = {
      data: {
        event_action: 'payment_added',
        event_category: 'ecommerce',
        event_label: paymentType,
        event_name: 'add_payment_details',
        enh_action: 'checkout_option',
        checkout_option: paymentType,
      },
      fields: EVENT_FIELDS,
      eventType: EventType.Link,
    };
    await this.trackEvent(eventData);
  }

  async enterShippingAddressLinkEvent(): Promise<void> {
    const eventData = {
      data: {
        active_checkout_panel: 'shipping',
        event_name: 'add_shipping_info',
        event_category: 'ecommerce',
        event_action: 'add_shipping_info',
        event_label: 'add_shipping_info',
        event_noninteraction: true,
        enh_action: 'checkout',

      },
      fields: EVENT_FIELDS,
      eventType: EventType.Link,
    };
    await this.trackEvent(eventData);
  }

  async updateShippingAddressLinkEvent(): Promise<void> {
    const eventData = {
      data: {
        event_name: 'update_address_details',
        event_category: 'ecommerce',
        event_action: 'address_updated',
        event_label: 'address_updated',
      },
      fields: EVENT_FIELDS,
      eventType: EventType.Link,
    };
    await this.trackEvent(eventData);
  }

  async selectShippingOptionLinkEvent(option: string): Promise<void> {
    const eventData = {
      data: {
        event_name: 'shipping_options',
        enh_action: 'checkout_option',
        event_category: 'ecommerce',
        event_action: 'shipping_options',
        event_label: option,
        checkout_option: option,
      },
      fields: EVENT_FIELDS,
      eventType: EventType.Link,
    };
    await this.trackEvent(eventData);
  }

  async signUpForNewsletterLinkEvent(): Promise<void> {
    const eventData = {
      data: {
        event_name: 'signup',
        event_category: 'signup',
        event_action: 'Email',
        event_label: 'shipping',
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };
    await this.trackEvent(eventData);
  }

  bopisStoreSearch(): void {
    const eventData = {
      data: {
        event_name: 'bopis_store_search',
        event_category: 'ecommerce',
        event_action: 'bopis',
        event_label: 'store_search',
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };
    this.trackEvent(eventData);
  }

  bopisStoreSelect(storeName: string): void {
    const eventData = {
      data: {
        event_name: 'bopis_store_selection',
        event_category: 'ecommerce',
        event_action: 'bopis',
        event_label: `store_selection_${storeName}`,
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };
    this.trackEvent(eventData);
  }

  bopisStoreOpenHoursToggle(storeName: string): void {
    const eventData = {
      data: {
        event_name: 'bopis_opening_hours',
        event_category: 'ecommerce',
        event_action: 'bopis',
        event_label: `store_opening_hours_toggle_${storeName}`,
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };
    this.trackEvent(eventData);
  }

  bopisStoreChange(storeName: string): void {
    const eventData = {
      data: {
        event_name: 'bopis_change_store',
        event_category: 'ecommerce',
        event_action: 'bopis',
        event_label: `change_store_selected_${storeName}`,
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };
    this.trackEvent(eventData);
  }

  bopisEditCartScreenViewEvent(cartItems: CartItem[]): void {
    const productIds = cartItems.map(item => item.productBaseId);
    const eventData = {
      data: {
        event_name: 'bopis_items_unavailable',
        event_category: 'ecommerce',
        event_action: 'bopis',
        event_label: productIds.toString(),
      },
      fields: LINK_FIELDS,
      eventType: EventType.View,
    };
    this.trackEvent(eventData);
  }

  postmatesTimeWindowSelected(value: string): void {
    const eventData = {
      data: {
        event_name: 'postmates_delivery_window',
        event_category: 'ecommerce',
        event_action: 'postmates_delivery_window',
        event_label: value,
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };
    this.trackEvent(eventData);
  }

  async editDeliveryFromPayment(): Promise<void> {
    const transaction = await this.transactionRepository.getTransaction();
    const selectedShippingMethodId = transaction && transaction.selectedShippingMethodId;
    const eventData = {
      data: {
        event_name: 'edit_delivery_details',
        event_category: 'ecommerce',
        event_action: 'edit_delivery_details',
        event_label: shippingOptionsMap[selectedShippingMethodId],
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };
    this.trackEvent(eventData);
  }

  selectDeliveryOptionEvent(deliveryOption: DeliveryOptions): void {
    const eventData = {
      data: {
        event_name: 'delivery_type',
        event_category: 'ecommerce',
        event_action: 'delivery_type',
        event_label: deliveryOptionsMap[deliveryOption],
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };
    this.trackEvent(eventData);
  }

  deliveryMethodChangedFromUnavailableItemsScreen(): void {
    const eventData = {
      data: {
        event_name: 'bopis_edit_delivery_type_unavailable_items',
        event_category: 'ecommerce',
        event_action: 'bopis',
        event_label: 'standard',
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };
    this.trackEvent(eventData);
  }

  async itemsRemovedFromUnavailableItemsScreen(currentStore: Store): Promise<void> {
    const transaction = await this.transactionRepository.getTransaction();
    const cartItems = transaction && transaction.cart && transaction.cart.cartItems;
    if (cartItems) {
      const unavailableItems = cartItems.filter(cartItem => {
        const item = currentStore.inventory.find(inv => inv.skuBaseId === cartItem.skuBaseId);
        return item && !item.isAvailable;
      });
      const productIds = unavailableItems.map(item => item.productBaseId);

      const eventData = {
        data: {
          event_name: 'bopis_remove_unavailable_items',
          event_category: 'ecommerce',
          event_action: 'bopis',
          event_label: productIds.toString(),
        },
        fields: LINK_FIELDS,
        eventType: EventType.Link,
      };
      this.trackEvent(eventData);
    }
  }

  salonCommissionOpenOverlay(): void {
    const eventData = {
      data: {
        event_name: 'salon_commission',
        event_category: 'salon_commission',
        event_action: 'salon_overlay',
        event_label: 'open',
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };

    this.trackEvent(eventData);
  }

  salonCommissionSearch(searchTerm: string): void {
    const eventData = {
      data: {
        event_name: 'salon_commission',
        event_category: 'salon_commission',
        event_action: 'salon_search',
        event_label: searchTerm,
      },
      fields: LINK_FIELDS,
      eventType: EventType.Link,
    };

    this.trackEvent(eventData);
  }
}
